import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * Top-level safety net so an uncaught render error shows a generic, non-leaking error page
 * instead of a blank screen. Never renders the raw error/stack to the user.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Unhandled application error", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="container" style={{ paddingTop: "64px" }}>
          <div className="alert alert--danger" role="alert">
            <strong>Something went wrong.</strong>
            <p>
              An unexpected error occurred. Your funds and orders are unaffected — all financial
              state lives onchain and on the server, never only in this page.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => window.location.assign("/")}
            >
              Return to the marketplace
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
